import type { FetchNotesParams, Folder, Note } from "../../api/notes";

export type SidebarFilter = "all" | "favourites" | "shared" | "recycle-bin" | "unfiled" | string;
export type NoteType = Note["type"];
export type NoteTypeFilter = "all" | NoteType;
export type ViewMode = "grid-large" | "grid-small" | "list" | "simple";
export type ContentView = "both" | "notes" | "folders";
export type SortMode = "created" | "updated" | "title" | "type";

export const VAULT_SNAPSHOT_KEY = "blackspace.vault.snapshot.v1";
export const VAULT_QUERY_CACHE_KEY = "blackspace.vault.query-cache.v1";
export const VAULT_NEW_NOTE_DRAFT_KEY = "blackspace.vault.new-note-draft.v1";
export const VAULT_NOTE_DRAFTS_KEY = "blackspace.vault.note-drafts.v1";
export const VAULT_SNAPSHOT_TTL_MS = 15 * 60 * 1000;
export const VAULT_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type VaultSnapshot = {
    notes: Note[];
    folders: Folder[];
    unfiledCount: number;
    favouritesCount: number;
    sharedTotalCount: number;
    totalNotes: number;
    hasMore: boolean;
    currentPage: number;
    searchQuery: string;
    activeFilter: SidebarFilter;
    activeType: NoteTypeFilter;
    viewMode: ViewMode;
    contentView?: ContentView;
    sortMode: SortMode;
    queryKey?: string;
    scrollY: number;
    selectedNoteId?: string | null;
    savedAt: number;
};

export type VaultQueryCacheEntry = {
    notes: Note[];
    totalNotes: number;
    hasMore: boolean;
    currentPage: number;
    savedAt: number;
};

export type NewNoteDraft = {
    title: string;
    content: string;
    tags: string;
    folder: string | null;
    type: NoteType;
    color?: string;
    savedAt: number;
};

export type OpenNoteDraft = {
    title: string;
    content: string;
    tags: string;
    folder: string | null;
    type: NoteType;
    color?: string;
    baseUpdatedAt: string;
    savedAt: number;
};

function fresh(savedAt: unknown, ttl = VAULT_DRAFT_TTL_MS) {
    return Date.now() - Number(savedAt || 0) <= ttl;
}

export function readVaultSnapshot(): VaultSnapshot | null {
    try {
        const raw = sessionStorage.getItem(VAULT_SNAPSHOT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as VaultSnapshot;
        if (!Array.isArray(parsed.notes) || !Array.isArray(parsed.folders)) return null;
        if (!fresh(parsed.savedAt, VAULT_SNAPSHOT_TTL_MS)) {
            sessionStorage.removeItem(VAULT_SNAPSHOT_KEY);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

export function writeVaultSnapshot(snapshot: VaultSnapshot) {
    try {
        sessionStorage.setItem(VAULT_SNAPSHOT_KEY, JSON.stringify(snapshot));
    } catch {
        // Snapshot restore is a polish layer; Vault can always fall back to a fresh load.
    }
}

export function vaultQueryKey(params: FetchNotesParams) {
    const { page: _page, limit: _limit, ...rest } = params;
    return JSON.stringify(Object.entries(rest).filter(([, value]) => value !== undefined && value !== null && value !== "").sort());
}

export function readVaultQueryCache(queryKey: string): VaultQueryCacheEntry | null {
    try {
        const raw = sessionStorage.getItem(VAULT_QUERY_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Record<string, VaultQueryCacheEntry>;
        const entry = parsed[queryKey];
        if (!entry || !Array.isArray(entry.notes)) return null;
        if (!fresh(entry.savedAt, VAULT_SNAPSHOT_TTL_MS)) return null;
        return entry;
    } catch {
        return null;
    }
}

export function writeVaultQueryCache(queryKey: string, entry: Omit<VaultQueryCacheEntry, "savedAt">) {
    try {
        const raw = sessionStorage.getItem(VAULT_QUERY_CACHE_KEY);
        const parsed = raw ? JSON.parse(raw) as Record<string, VaultQueryCacheEntry> : {};
        const next = {
            ...parsed,
            [queryKey]: { ...entry, savedAt: Date.now() },
        };
        const pruned = Object.fromEntries(
            Object.entries(next)
                .sort(([, a], [, b]) => b.savedAt - a.savedAt)
                .slice(0, 18)
        );
        sessionStorage.setItem(VAULT_QUERY_CACHE_KEY, JSON.stringify(pruned));
    } catch {
        // Query cache is only a smoothness layer.
    }
}

export function readNewNoteDraft(): NewNoteDraft | null {
    try {
        const raw = localStorage.getItem(VAULT_NEW_NOTE_DRAFT_KEY);
        if (!raw) return null;
        const draft = JSON.parse(raw) as NewNoteDraft;
        if (!fresh(draft.savedAt)) {
            localStorage.removeItem(VAULT_NEW_NOTE_DRAFT_KEY);
            return null;
        }
        return draft;
    } catch {
        return null;
    }
}

export function writeNewNoteDraft(draft: Omit<NewNoteDraft, "savedAt">) {
    try {
        localStorage.setItem(VAULT_NEW_NOTE_DRAFT_KEY, JSON.stringify({ ...draft, savedAt: Date.now() }));
    } catch {
        // Draft persistence should never block writing.
    }
}

export function clearNewNoteDraft() {
    try {
        localStorage.removeItem(VAULT_NEW_NOTE_DRAFT_KEY);
    } catch {
        // Ignore storage availability issues.
    }
}

function readOpenNoteDrafts(): Record<string, OpenNoteDraft> {
    try {
        const raw = localStorage.getItem(VAULT_NOTE_DRAFTS_KEY);
        return raw ? JSON.parse(raw) as Record<string, OpenNoteDraft> : {};
    } catch {
        return {};
    }
}

export function readOpenNoteDraft(noteId: string, baseUpdatedAt: string): OpenNoteDraft | null {
    const draft = readOpenNoteDrafts()[noteId];
    if (!draft || draft.baseUpdatedAt !== baseUpdatedAt || !fresh(draft.savedAt)) return null;
    return draft;
}

export function writeOpenNoteDraft(noteId: string, draft: Omit<OpenNoteDraft, "savedAt">) {
    try {
        const drafts = readOpenNoteDrafts();
        drafts[noteId] = { ...draft, savedAt: Date.now() };
        localStorage.setItem(VAULT_NOTE_DRAFTS_KEY, JSON.stringify(drafts));
    } catch {
        // Draft persistence should never block writing.
    }
}

export function clearOpenNoteDraft(noteId: string) {
    try {
        const drafts = readOpenNoteDrafts();
        delete drafts[noteId];
        localStorage.setItem(VAULT_NOTE_DRAFTS_KEY, JSON.stringify(drafts));
    } catch {
        // Ignore storage availability issues.
    }
}
